import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { NewsletterAPI, MaintenanceAPI } from '../api';
import { UserContext } from '../context/UserContext';
import './home.css';

export default function MaintenanceLandingExtended({ status }) {
  const { setUser } = useContext(UserContext);
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Newsletter modal state
  const [newsOpen, setNewsOpen] = useState(false);
  const [nlFirstName, setNlFirstName] = useState('');
  const [nlEmail, setNlEmail] = useState('');
  const [nlTwitch, setNlTwitch] = useState('');
  const [nlTiktok, setNlTiktok] = useState('');
  const [nlYoutube, setNlYoutube] = useState('');
  const [nlSource, setNlSource] = useState('');
  const [nlConsent, setNlConsent] = useState(false);
  const [nlError, setNlError] = useState('');
  const [nlSuccess, setNlSuccess] = useState('');
  const [nlLoading, setNlLoading] = useState(false);

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setUser(data.user);
      if (data.token) localStorage.setItem('token', data.token);
      const roles = Array.isArray(data.user?.roles) ? data.user.roles : [];
      const privileged = roles.includes('admin') || roles.includes('owner') || data.user?.role === 'admin' || data.user?.role === 'owner';
      nav(privileged ? '/dashboard' : '/');
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Login fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const [localUntil, setLocalUntil] = useState(null);
  const untilText = (localUntil || status?.until) ? new Date(localUntil || status.until).toLocaleString() : null;
  const targetTs = useMemo(() => {
    const src = localUntil || status?.until;
    return src ? new Date(src).getTime() : null;
  }, [status?.until, localUntil]);

  function getRemainingParts(target) {
    if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const now = Date.now();
    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds };
  }

  const [remain, setRemain] = useState(() => getRemainingParts(targetTs));
  useEffect(() => {
    if (!targetTs) return;
    setRemain(getRemainingParts(targetTs));
    const id = setInterval(() => {
      setRemain(getRemainingParts(targetTs));
    }, 1000);
    return () => clearInterval(id);
  }, [targetTs]);

  // Handle behavior when countdown hits zero
  const behavior = status?.extendedCountdownBehavior || 'none';
  const [fired, setFired] = useState(false);
  useEffect(() => {
    if (!targetTs) return;
    const zero = targetTs && Date.now() >= targetTs;
    if (!zero) {
      if (fired) setFired(false);
      return;
    }
    if (fired) return;
    if (behavior === 'autoExtend' || behavior === 'off') {
      setFired(true);
      // Trigger backend behavior via status fetch
      (async () => {
        try {
          const s = await MaintenanceAPI.status();
          if (!s?.enabled) {
            // maintenance ended -> reload to normal site
            window.location.reload();
            return;
          }
          if (behavior === 'autoExtend' && s.until) {
            const nextTs = new Date(s.until).getTime();
            if (nextTs > Date.now()) setLocalUntil(s.until);
          }
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [targetTs, behavior, fired]);

  function openNewsletter() {
    setNewsOpen(true);
    setNlError('');
    setNlSuccess('');
  }

  function closeNewsletter() {
    setNewsOpen(false);
    // optional: keep values so user doesn’t lose input on accidental close
  }

  function validateEmail(v) {
    return /.+@.+\..+/.test(String(v || '').trim());
  }

  async function submitNewsletter(e) {
    if (e) e.preventDefault();
    setNlError(''); setNlSuccess('');
    if (!validateEmail(nlEmail)) {
      setNlError('Bitte eine gültige E‑Mail-Adresse angeben.');
      return;
    }
    if (!nlConsent) {
      setNlError('Bitte stimme dem Erhalt des Newsletters zu.');
      return;
    }
    try {
      setNlLoading(true);
      const payload = {
        email: nlEmail,
        firstName: nlFirstName,
        twitch: nlTwitch,
        tiktok: nlTiktok,
        youtube: nlYoutube,
        source: nlSource,
        consent: true,
      };
      await NewsletterAPI.subscribe(payload);
      setNlSuccess('Danke! Bitte prüfe dein Postfach und bestätige die Anmeldung per E‑Mail.');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Anmeldung fehlgeschlagen';
      setNlError(msg);
    } finally {
      setNlLoading(false);
    }
  }

  function unsubscribeNewsletter() {
    const email = (nlEmail || '').trim();
    if (!validateEmail(email)) {
      setNlError('Bitte gib zum Abmelden eine gültige E‑Mail ein.');
      return;
    }
    setNlError(''); setNlSuccess('');
    setNlLoading(true);
    NewsletterAPI.unsubscribe({ email })
      .then(() => setNlSuccess('Du wurdest vom Newsletter abgemeldet.'))
      .catch(err => setNlError(err?.response?.data?.error || err?.message || 'Abmeldung fehlgeschlagen'))
      .finally(() => setNlLoading(false));
  }

  return (
    <div className="home-page" style={{ width:'100vw', margin:0, padding:0 }}>
      {/* HERO – gleiche Section wie auf der Startseite, aber ohne Buttons */}
      <section className="home-hero">
        <svg className="hero-bg-svg" viewBox="0 0 1440 400" aria-hidden="true" >
          <circle cx="200" cy="120" r="4" fill="#1C63D6" />
          <circle cx="400" cy="80"  r="3" fill="#8F5DD4" />
          <circle cx="900" cy="200"  r="5" fill="#32CD89" />
          <circle cx="1200" cy="100" r="4" fill="#FFD700" />
          <line x1="200" y1="120" x2="400"  y2="80"  stroke="#1C63D6" strokeWidth="2" />
          <line x1="400" y1="80"  x2="900"  y2="200" stroke="#8F5DD4" strokeWidth="2" />
          <line x1="900" y1="200" x2="1200" y2="100" stroke="#32CD89" strokeWidth="2" />
        </svg>

        <div className="hero-content" style={{width:'100vw',margin:0,padding:0}}>
          <div className="hero-logo-bar" aria-hidden="true">
            <img className="hero-logo-img" src="/apn-3d-logo.png" alt="AP-N Logo" />
          </div>

          <h1 className="hero-title">Gemeinsam. Sichtbar. Stark.</h1>
          <p className="hero-desc">
            Die exklusive Community für ambitionierte Creator. <br/>
            Qualität, Teamgeist und gegenseitige Unterstützung <br/>
            stehen hier im Mittelpunkt.
          </p>
          {/* Keine Buttons im erweiterten Wartungsmodus */}
        </div>

        <div className="hero-float1" />
        <div className="hero-float2" />
        <div className="hero-float3" />
      </section>

      {/* Prominente Ankündigung unter dem Hero */}
      <section style={{ width:'100%', maxWidth:'1200px', margin:'12px auto 0', padding:'0 16px' }}>
        <div style={{
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          textAlign:'center',
          padding:'18px 20px',
          border:'1px solid rgba(255,215,0,0.55)',
          background:'linear-gradient(90deg, rgba(255,215,0,0.18), rgba(143,93,212,0.14))',
          borderRadius:14,
          boxShadow:'0 10px 28px rgba(255,215,0,0.22)'
        }}>
          <h2 className="hero-title" style={{ margin:0, fontSize:'clamp(1.6rem, 3.5vw, 2.4rem)' }}>
            Die Zukunft für alle Streamer und Content-Creator beginnt bald!
          </h2>
        </div>
        {(status?.message || targetTs) && (
          <div style={{ maxWidth:900, margin:'10px auto 0', textAlign:'center' }}>
            {status?.message && (
              <p className="hero-desc" style={{ margin:'0 auto 6px', opacity:0.95 }}>{status.message}</p>
            )}
            {targetTs && (
              <>
                { (behavior === 'text' && targetTs && Date.now() >= targetTs) ? (
                  <p className="hero-desc" style={{ margin:'6px auto 10px', opacity:0.95 }}>
                    {status?.extendedCountdownText || 'Jetzt gleich sofort...'}
                  </p>
                ) : (
                  <>
                    <p className="hero-desc" style={{ margin:'6px auto 10px', opacity:0.95 }}>
                      Wir zählen die Tage, Stunden, Minuten und Sekunden …
                    </p>
                    <div style={{ display:'flex', alignItems:'stretch', justifyContent:'center', gap:20, flexWrap:'wrap' }} aria-live="polite">
                      {[
                        { label: 'Tage', value: String(remain.days) },
                        { label: 'Stunden', value: String(remain.hours).padStart(2,'0') },
                        { label: 'Minuten', value: String(remain.minutes).padStart(2,'0') },
                        { label: 'Sekunden', value: String(remain.seconds).padStart(2,'0') },
                      ].map((b, i) => (
                        <div key={b.label+String(i)} style={{
                          minWidth:180,
                          padding:'22px 24px',
                          borderRadius:18,
                          background:'linear-gradient(180deg, rgba(28,99,214,0.26), rgba(143,93,212,0.18))',
                          border:'1px solid rgba(255,255,255,0.14)',
                          boxShadow:'0 18px 44px rgba(0,0,0,0.32)'
                        }}>
                          <div style={{
                            fontSize:'clamp(44px, 7.5vw, 84px)',
                            fontWeight:900,
                            letterSpacing:1.2,
                            fontVariantNumeric:'tabular-nums',
                            lineHeight:1,
                            color:'#ffffff'
                          }}>
                            {b.value}
                          </div>
                          <div style={{ fontSize:16, opacity:0.95, marginTop:10 }}>{b.label}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </section>

      {/* Platz für erweiterte Inhalte während des Wartungsmodus */}
      <section style={{ width:'100%', maxWidth: 1200, margin:'0 auto', padding:'0 16px' }}>
        <div style={{
          display:'grid',
          gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))',
          gap:18,
          marginTop: 16
        }}>
          {(() => {
            const defaults = [
              { title: 'Was passiert gerade?', body: 'Wir führen Wartungsarbeiten durch, damit AP‑N noch stabiler und schneller wird.' },
              { title: 'Bleib auf dem Laufenden', body: 'Folge unseren offiziellen Kanälen oder schaue später erneut vorbei.' },
              { title: 'Neuerungen', body: 'Hier kannst du demnächst lesen, was sich ändert und welche Vorteile das bringt.' },
              { title: 'Support', body: 'Bei Fragen erreichst du uns über die bekannten Kanäle.' },
            ];
            const tiles = Array.isArray(status?.extendedContent) && status.extendedContent.length
              ? status.extendedContent.slice(0,4)
              : defaults;
            return tiles.map((t, idx) => (
              <div key={idx} className="highlight-card" style={{ alignItems:'flex-start', textAlign:'left' }}>
                <h2 className="highlight-title">{t?.title || defaults[idx]?.title}</h2>
                <p className="highlight-desc">{t?.body || defaults[idx]?.body}</p>
              </div>
            ));
          })()}
        </div>
      </section>

      {/* Newsletter CTA between tiles and login */}
      <section style={{ width:'100%', maxWidth: 900, margin:'18px auto 8px auto', padding:'0 16px' }}>
        <div style={{
          border:'1px solid rgba(255,255,255,0.14)',
          background:'linear-gradient(180deg, rgba(28,99,214,0.10), rgba(143,93,212,0.08))',
          borderRadius:14,
          padding:'18px 20px',
          boxShadow:'0 10px 28px rgba(0,0,0,0.22)',
          textAlign:'center'
        }}>
          <h3 className="section-subtitle" style={{ margin:'6px 0 8px', fontSize:'1.3rem' }}>Bleib informiert</h3>
          <p className="hero-desc" style={{ margin:'0 auto 14px', maxWidth:760 }}>
            Melde dich für unseren Newsletter an, und verpasse keine Information mehr zum AP-Network. Schon bald gibt’s mehr!
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button className="hero-btn-primary" onClick={openNewsletter}>
              Newsletter anmelden
            </button>
            <button className="hero-btn-secondary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Nach Oben
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Modal */}
      {newsOpen && (
        <div role="dialog" aria-modal="true" aria-labelledby="nl-title" style={{
          position:'fixed', inset:0, zIndex:9999,
          background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:16
        }}>
          <div style={{
            width:'100%', maxWidth:560,
            background:'rgba(10,20,40,0.98)', border:'1px solid #223', borderRadius:12,
            boxShadow:'0 20px 60px rgba(0,0,0,0.5)', color:'#fff'
          }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'1px solid #223' }}>
              <h3 id="nl-title" className="section-subtitle" style={{ margin:0, fontSize:'1.2rem' }}>Newsletter abonnieren</h3>
              <button onClick={closeNewsletter} className="hero-btn-secondary" style={{ padding:'6px 10px' }}>Abbrechen</button>
            </div>
            <form onSubmit={submitNewsletter} style={{ padding:16, display:'grid', gap:12 }}>
              <p className="hero-desc" style={{ margin:'0 0 4px 0' }}>
                Trage dich für den AP-Network Newsletter ein und erhalte Updates direkt in dein Postfach.<br/>
                Hinweis: Nach der Anmeldung erhältst du eine E-Mail mit einem Bestätigungslink. Erst nach Klick auf diesen Link ist deine Anmeldung gültig. Alle Zusatzfelder sind optional.
              </p>

              <div style={{ display:'grid', gap:10 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>Vorname (optional)</label>
                  <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                    placeholder="Max"
                    value={nlFirstName}
                    onChange={e => setNlFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>E‑Mail*</label>
                  <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                    type="email"
                    required
                    placeholder="dein.name@example.com"
                    value={nlEmail}
                    onChange={e => setNlEmail(e.target.value)}
                  />
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:10 }}>
                  <div>
                    <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>Twitch‑Name (optional)</label>
                    <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                      placeholder="z.B. apn_creator"
                      value={nlTwitch}
                      onChange={e => setNlTwitch(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>TikTok‑Name (optional)</label>
                    <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                      placeholder="z.B. apn_creator"
                      value={nlTiktok}
                      onChange={e => setNlTiktok(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>YouTube‑Name (optional)</label>
                    <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                      placeholder="z.B. apn_creator"
                      value={nlYoutube}
                      onChange={e => setNlYoutube(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display:'block', fontSize:12, opacity:.9, marginBottom:4 }}>Wie bist du auf AP‑Network aufmerksam geworden? (optional)</label>
                  <input className="hero-btn-secondary" style={{ width:'100%', padding:'10px 12px' }}
                    placeholder="Freunde, Social Media, Events, …"
                    value={nlSource}
                    onChange={e => setNlSource(e.target.value)}
                  />
                </div>
              </div>

              <label style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
                <input type="checkbox" checked={nlConsent} onChange={e => setNlConsent(e.target.checked)} />
                <span>
                  Ich willige ein, den Newsletter per E‑Mail zu erhalten. Hinweise in der{' '}
                  <Link to="/datenschutz" style={{ color:'#32CD89', textDecoration:'underline' }}>Datenschutzerklärung</Link>.
                </span>
              </label>

              <div style={{ fontSize:12, opacity:.85 }}>
                Double-Opt-In: Nach dem Absenden erhältst du eine E‑Mail mit einem Bestätigungslink. Erst nach Klick auf diesen Link ist deine Anmeldung aktiv. Du kannst dich jederzeit wieder abmelden. Mehr Informationen findest du in unserer{' '}
                <Link to="/datenschutz" style={{ color:'#32CD89', textDecoration:'underline' }}>Datenschutzerklärung</Link>.
              </div>

              {nlError && (
                <div style={{ color:'tomato', border:'1px solid rgba(255,99,71,0.4)', background:'rgba(255,99,71,0.08)', padding:'8px 12px', borderRadius:8 }}>
                  {nlError}
                </div>
              )}
              {nlSuccess && (
                <div style={{ color:'#32CD89', border:'1px solid rgba(50,205,137,0.4)', background:'rgba(50,205,137,0.08)', padding:'8px 12px', borderRadius:8 }}>
                  {nlSuccess}
                </div>
              )}

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', flexWrap:'wrap' }}>
                <button type="button" className="hero-btn-secondary" onClick={unsubscribeNewsletter} disabled={!!nlLoading}>vom Newsletter abmelden</button>
                <button type="button" className="hero-btn-secondary" onClick={closeNewsletter} disabled={!!nlLoading}>Abbrechen</button>
                <button type="submit" className="hero-btn-primary" disabled={!!nlLoading}>{nlLoading ? 'Bitte warten …' : 'Jetzt anmelden & E‑Mail erhalten'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section style={{ width:'100%', maxWidth: 520, margin:'24px auto 40px auto', padding:'0 16px' }}>
        <form className="highlight-card" style={{ gap: 16, padding: 24 }} onSubmit={e => { e.preventDefault(); handleLogin(); }}>
          <h2 className="highlight-title" style={{ marginBottom: 4 }}>Direktlogin für Mitglieder</h2>
          <input
            className="hero-btn-secondary"
            style={{ padding:'12px 16px', fontSize:'1rem' }}
            placeholder="E-Mail"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div style={{ position:'relative', height:48 }}>
            <input
              className="hero-btn-secondary"
              style={{ padding:'12px 16px', fontSize:'1rem', width:'100%' }}
              type={showPassword ? 'text' : 'password'}
              placeholder="Passwort"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              onClick={() => setShowPassword(v => !v)}
              style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:0, cursor:'pointer', color:'#32CD89', fontSize:'1.2em', padding:'0 4px', lineHeight:1 }}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {error && <div style={{ color:'tomato', fontSize:14 }}>{error}</div>}
          <button type="submit" className="hero-btn-primary" disabled={loading}>
            {loading ? 'Einloggen …' : 'Einloggen'}
          </button>
        </form>
        <div style={{ fontSize:12, opacity:.75, textAlign:'center', marginTop:8 }}>
          Hinweis: Während des Wartungsmodus können sich nur Administratoren anmelden, sofern die allgemeine Anmeldung deaktiviert wurde.
        </div>
      </section>
    </div>
  );
}
